// The land crew's key card: what each fighting compartment actually answers
// to. Smoke, field repair, the artillery plot and overdrive come with every
// armed hull (the server's steerLandVehicle works them off any land type),
// but two keys belong to one hull each: only the Abrams' 120 mm swaps
// between AP and HE (the MUTT's recoilless fires HEAT alone, and the AA
// hulls carry no main gun at all), and only the Bradley carries the
// wire-guided missile. A card that promised every tanker "B missile" sent
// an Abrams commander hunting for a launcher the hull never had. B on the
// Abrams is her coaxial .50 instead.

// hasAmmoToggle: the hull's main gun loads both AP and HE (the same test the
// crew panel uses to show its AP/HE chip).
export const landHasAmmoToggle = (t) => (t?.mainGun?.heDamage ?? 0) > 0 && !t?.mainGun?.heat;

// landRoundLabel names what a single-nature gun fires, for the crew panel's
// greyed shell chip: the MUTT's recoilless is HEAT, the Bradley and the
// sky-sweeper fire their autocannon ("25MM", "20MM" — read off the caliber's
// own label in `calibers`). A hull that swaps AP/HE gets null: its chip is
// live and reads the loaded nature instead. The chip once said "HE" on all of
// them, a nature not one of them ever loaded.
export function landRoundLabel(t, calibers = []) {
  if (landHasAmmoToggle(t)) return null;
  if (t?.mainGun?.heat) return "HEAT";
  const cal = t?.aa?.caliber && calibers.find((c) => c.name === t.aa.caliber);
  if (cal) {
    const mm = /(\d+)\s*mm/i.exec(cal.label ?? "");
    return mm ? `${mm[1]}MM` : (cal.label ?? cal.name).toUpperCase();
  }
  return (t?.mainGun?.apDamage ?? 0) > 0 ? "AP" : "—";
}

// landHasMissile: the hull carries a wire-guided missile (B).
export const landHasMissile = (t) => (t?.atgm?.damage ?? 0) > 0;

// landHasCoax: the hull carries a coaxial machine gun (held on B).
export const landHasCoax = (t) => !!t?.coax?.caliber;

// Two of the crew's keys are not the crew's own: B (missile / coax) rides the
// pilot's rebindable "bomb" action and F rides "bail", so a player who moved
// bomb to N fires the Bradley's missile on N. A clause on one of those names
// the action (kb), and the caller paints its live key; the rest — Z X C R T
// G, the conning keys main.js keeps for every hull — are fixed letters. These
// are the shipped defaults, for a caller with no keymap to ask (the tests).
export const CREW_DEFAULT_KEYS = { bomb: "B", bail: "F" };

// landCrewClauses lists the crew-action clauses for hull type t, in the order
// the hint bar and the ROLLING brief read them: { key } for a fixed letter,
// { kb } for a rebindable action, and the clause's text. With no hull (the
// hint bar before a player has picked one) only the universal keys are
// promised. The verbs are the caller's: the hint bar says "swap AP/HE" and
// "bail out", the brief says "AP/HE" and "bails out".
export function landCrewClauses(t, { swap = "AP/HE", bail = "bails out" } = {}) {
  return [
    { key: "Z", text: "gunsight" },
    landHasAmmoToggle(t) ? { key: "X", text: swap } : null,
    landHasMissile(t) ? { kb: "bomb", text: "missile" } : landHasCoax(t) ? { kb: "bomb", text: "coax MG" } : null,
    { key: "C", text: "smoke" },
    { key: "R", text: "repair" },
    { key: "T", text: "artillery" },
    { key: "G", text: "overdrive" },
    { kb: "bail", text: bail },
  ].filter(Boolean);
}

// landCrewKeys: the clauses as one plain line, for the ROLLING brief (a
// banner that lives six seconds — it reads the live keys once, when written).
// keyFor(action) answers the player's key for a rebindable clause.
export function landCrewKeys(t, { keyFor = (id) => CREW_DEFAULT_KEYS[id], ...verbs } = {}) {
  return landCrewClauses(t, verbs)
    .map((c) => `${c.kb ? keyFor(c.kb) : c.key} ${c.text}`)
    .join(" · ");
}

// landCrewKeysHTML: the clauses for the hint bar, every key in gold. The
// rebindable ones go out as <b data-kb> tokens carrying the default letter;
// keybindings.js repaintHints() rewrites them from the live map, on boot and
// on every rebind, the way the air bar has always worked.
export function landCrewKeysHTML(t, verbs) {
  return landCrewClauses(t, verbs)
    .map((c) => (c.kb ? `<b data-kb="${c.kb}">${CREW_DEFAULT_KEYS[c.kb]}</b> ${c.text}` : `<b>${c.key}</b> ${c.text}`))
    .join(" · ");
}

// landRollingOpener: the one line of doctrine the ROLLING brief leads with,
// worded for the hull's kit, not the Abrams' for everyone. A stabilized main
// gun with plate to angle fights from the folds; an unstabilized gun on a
// soft truck halts to shoot and moves before the answer arrives; a hull whose
// only gun is a flak mount (the sky-sweeper) hunts the strike wings; and a
// hull with a wire-guided missile is told the wire flies where the crosshair
// sits, so it holds the sight on until the strike. keyFor names the missile's
// live key on a keyboard; a phone (touch) is told the ATGM button instead.
export function landRollingOpener(t, { touch = false, keyFor = (id) => CREW_DEFAULT_KEYS[id] } = {}) {
  if (landHasMissile(t)) {
    const launch = touch ? "tap ATGM" : `launch on ${keyFor("bomb")}`;
    return `Your punch is the wire-guided missile: ${launch} and hold the crosshair on the target until it strikes — the autocannon is for soft hulls.`;
  }
  if (!t?.mainGun && t?.aa) {
    return "Your job is the sky: lead the strike planes with the guns and shred soft hulls between raids — your plate stops nothing a tank fires.";
  }
  if (t?.mainGun && !t?.stabilized) {
    return "Halt to fire — the gun isn't stabilized and the round drops, so hold over by the range marks — then move before they answer: your skin stops nothing.";
  }
  if (t?.mainGun) {
    return "Your round drops: hold over by the range marks; angle your armor and use the folds.";
  }
  return "Hold over by the range marks and use the folds.";
}
